import { gql, NetworkStatus, useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useEventsSocket } from '../../../store';
import type { EventFilters } from '../store';
import type {
  GetEventsQuery,
  GetEventsQueryVariables // eslint-disable-next-line import/no-internal-modules
} from './__generated__/use-events';
import type { Maybe, ScheduledEvent, SearchableScheduledEventFilterInput } from './type';
import { useEventSubscriptions } from './use-event-subscriptions';
import { evictEventsFromCache, transformFiltersToVariables } from './utils';

export const eventFieldsFragment = gql`
  fragment EventFields on AssetWithEvent {
    location
    location_name
    location_secondary_id
    backgroundUrl
    endTime
    eventType
    name
    eid
    has_asset
    high_priority
    isLive
    muxStreamUrl
    startTime
    stream_facebook
    stream_rtmp
    stream_youtube
    stream_zoom
    stream_live
    status
    visibility
    hidden
    checked_user
    support_notes
    verifier_email
    operator_name
    is_first_event
    audioContact {
      id
      first_name
      last_name
      email
      phone_number
      preferred_contact_method
      phone_type
      notes
      priority
    }
    receive_iso_recordings
    audio_cue_sheet_url
    client {
      backgroundUrl
      cid
      organizationName
      timezone
    }
  }
`;

const QUERY = gql`
  query GetEvents(
    $input: GetEventsInput!
  ) {
    getEvents(input: $input) {
      page
      limit
      success
      has_more
      result {
        ...EventFields
      }
    }
  }
  ${eventFieldsFragment}
`;

export interface GraphqlEventFilters extends EventFilters {
  eventIds?: string[];
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useEvents = (
  filters: GraphqlEventFilters,
  additionalFilters?: Maybe<SearchableScheduledEventFilterInput>,
  handlers: Omit<Parameters<typeof useEventSubscriptions>[0], 'onAdd'> = {}
) => {
  const { input } = transformFiltersToVariables(filters);

  // Input takes precedence!
  const mergedInput = { ...additionalFilters, ...input, page: 1 };

  /*
   * Callers rebuild `input`/`additionalFilters` every render, so keying the memo
   * on their identity produced a NEW `variables` each render, which made Apollo
   * re-run the base (page 1) query and reset pagination — loadMore looped 1,2,1,2
   * and never advanced. Key by VALUE so `variables` is stable while the filters are.
   */
  const variablesKey = JSON.stringify(mergedInput);

  const variables = useMemo(
    () => ({ input: mergedInput }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [variablesKey]
  );

  /*
   * `ids` is deliberately excluded from the cache keyArgs so the websocket's
   * by-ids fetchMore merges into the paginated list. The cost is that every
   * id-scoped query shares ONE cache entry regardless of which ids it asked
   * for, so cache-first happily answers a request for [A, B] with the result
   * stored for [A] — that's why a just-favorited event only showed up after a
   * reload. Id-scoped queries therefore revalidate; the paginated list, whose
   * key does reflect its filters, stays on cache-first.
   */
  const idScoped = (input?.ids?.length ?? 0) > 0;

  const { client, data, loading, fetchMore, error, refetch, networkStatus } = useQuery<
    GetEventsQuery,
    GetEventsQueryVariables
  >(QUERY, {
    variables,
    fetchPolicy: idScoped ? 'cache-and-network' : 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: input?.ids?.length === 0
  });

  const onWebsockethandler = (ids: number[]): void => {
    const scopedIds = variables.input.ids;
    const relevant = scopedIds?.length ? ids.filter((id) => scopedIds.includes(id)) : ids;

    if (!relevant.length) {
      return;
    }

    const input_ = { ...variables.input, ids: relevant, ignore_pagination: true, page: data?.getEvents?.page, has_more: data?.getEvents?.has_more };

    // check status and add to cache
    void fetchMore({ variables: { input: input_ }  }).then(({ data: websocketData }) => {

      if (!(<GetEventsQuery>websocketData).getEvents?.result.length) {
        evictEventsFromCache(client, relevant);
      }
    });

  };

  /*
   * Kept current so a queued flush runs against the page and filters of the moment it fires,
   * rather than whatever they were when the first frame of the burst landed.
   */
  const latestFetch = useRef(onWebsockethandler);

  latestFetch.current = onWebsockethandler;

  const addEvents = (ids: number[]): void => {
    const input_ = { ...variables.input, ids, ignore_pagination: true, page: data?.getEvents?.page, has_more: data?.getEvents?.has_more };

    void fetchMore({ variables: { input: input_ } }).then(({ data: websocketData }) => {

      if (!(<GetEventsQuery>websocketData).getEvents?.result.length) {
        evictEventsFromCache(client, ids);
      }
    });

  };

  useEventSubscriptions({
    ...handlers
    // onAdd: onWebsockethandler,

    // onUpdate: onWebsockethandler
  });

  const socket = useEventsSocket();
  const skipped = input?.ids?.length === 0;

  const latestRefetch = useRef(refetch);

  latestRefetch.current = refetch;

  useEffect(() => {
    if (skipped) {
      return undefined;
    }

    /* Coming back to a list that missed updates while it was away: reconcile once. */
    if (socket.consumeDirty(variablesKey)) {
      void latestRefetch.current();
    }

    return socket.subscribe(variablesKey, (message) => {
      if (message.kind === 'events') {
        latestFetch.current(message.ids);
      } else if (message.kind === 'resync' && socket.consumeDirty(variablesKey)) {
        void latestRefetch.current();
      }
    });
  }, [socket, variablesKey, skipped]);

  const hasMore = !!data?.getEvents && !!data.getEvents.has_more;

  const value = useMemo(
    () => data?.getEvents?.result.filter((v): v is ScheduledEvent => {
      const isValidEvent = Boolean(v?.eid) && Boolean(v?.has_asset);

      return Boolean(v) && isValidEvent;
    }),
    [data]
  );

  return {
    events: value,
    error,
    refetch,
    loading: loading || networkStatus === NetworkStatus.fetchMore,
    hasMore,
    addEvents,
    loadMore: useCallback(() => {

      if (!hasMore) {
        return;
      }

      void fetchMore<GetEventsQuery, GetEventsQueryVariables>({
        variables: {
          // make the api send the page
          input: {
            ...variables.input,
            page: data.getEvents?.page ? data.getEvents.page + 1 : data.getEvents?.page
          }
        }
      });
    }, [data?.getEvents, hasMore, fetchMore, variables])
  };
};

export type UseEventsHook = typeof useEvents;
